/**
 * QR Codes Settings Page
 *
 * Management page for styled QR codes with the designer component.
 * Grid view of all QR codes with create/edit/delete functionality.
 */

import { useState, useEffect, useRef } from 'react';
import { QrCode, Plus, Trash2, Copy, BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { QRCodeDesigner, type QRCodeDesignerHandle } from '@/components/qr/QRCodeDesigner';
import { UnitQrCodeList } from '@/components/qr/UnitQrCodeList';
import { useQRCodes } from '@/hooks/useQRCodes';
import { useQRLogos } from '@/hooks/useQRLogos';
import { useRBAC } from '@/contexts/RBACContext';
import type { QRCode, QRConfig } from '@/types/qrCode';

// App domain for building redirect URLs
const APP_DOMAIN = window.location.origin;

function getRedirectUrl(shortCode: string): string {
  return `${APP_DOMAIN}/api/v1/qr/${shortCode}`;
}

export function SettingsQRCodes() {
  const { isSuperadmin } = useRBAC();
  const { qrCodes, isLoading, isSubmitting, fetchQRCodes, createQRCode, updateQRCode, deleteQRCode, saveQRImage, resetStats } = useQRCodes();
  const { logos, fetchLogos, uploadLogo, isUploading, deleteLogo } = useQRLogos();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingQR, setEditingQR] = useState<QRCode | null>(null);
  const [formLabel, setFormLabel] = useState('');
  const [formDestination, setFormDestination] = useState('');
  const [formConfig, setFormConfig] = useState<QRConfig>({});
  const designerRef = useRef<QRCodeDesignerHandle>(null);

  useEffect(() => {
    fetchQRCodes();
    fetchLogos();
  }, [fetchQRCodes, fetchLogos]);

  const openCreate = () => {
    setEditingQR(null);
    setFormLabel('');
    setFormDestination('');
    setFormConfig({ fgColor: '#8FB560', bgColor: '#111111' });
    setDrawerOpen(true);
  };

  const openEdit = (qr: QRCode) => {
    setEditingQR(qr);
    setFormLabel(qr.label);
    setFormDestination(qr.destination_url);
    setFormConfig(qr.qr_config || {});
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!formLabel.trim() || !formDestination.trim()) {
      toast.error('Label and destination URL are required');
      return;
    }

    if (editingQR) {
      const result = await updateQRCode(editingQR.id, {
        label: formLabel,
        destination_url: formDestination,
        qr_config: formConfig,
      });
      if (result) {
        setDrawerOpen(false);
        fetchQRCodes();
      }
    } else {
      const result = await createQRCode({
        label: formLabel,
        destination_url: formDestination,
        qr_config: formConfig,
      });
      if (result) {
        setDrawerOpen(false);
        fetchQRCodes();
      }
    }
  };

  const handleDelete = async (qr: QRCode) => {
    if (!confirm(`Delete QR code "${qr.label}"? This cannot be undone.`)) return;
    const ok = await deleteQRCode(qr.id);
    if (ok) fetchQRCodes();
  };

  const handleSaveImage = async (blob: Blob) => {
    if (!editingQR) return;
    await saveQRImage(editingQR.id, blob);
    fetchQRCodes();
  };

  const handleResetStats = async (qr: QRCode) => {
    if (!confirm(`Reset scan count for "${qr.label}" to 0?`)) return;
    const ok = await resetStats(qr.id);
    if (ok) fetchQRCodes();
  };

  const copyUrl = (shortCode: string) => {
    navigator.clipboard.writeText(getRedirectUrl(shortCode));
    toast.success('QR redirect URL copied');
  };

  // The URL that the QR code encodes (the redirect URL for scan tracking)
  const qrValue = editingQR
    ? getRedirectUrl(editingQR.short_code)
    : formDestination || 'https://app.hollywoodtrucks.com';

  return (
    <div className="space-y-8">
      {/* Header - stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">QR Codes</h2>
          <p className="text-sm text-muted-foreground">
            Every QR in the system — styled shortcuts and per-unit check-in QRs.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#8FB560] hover:bg-[#7a9d51] self-start sm:self-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create QR Code
        </Button>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Styled QR Codes
          </h3>
          <p className="text-xs text-muted-foreground">
            Custom-styled QRs with scan tracking. Each one redirects via a
            short code under <code className="text-xs">/api/v1/qr/&lt;code&gt;</code>.
          </p>
        </div>
      </div>

      {/* QR Code Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : qrCodes.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <QrCode className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">No QR codes yet</p>
          <Button onClick={openCreate} variant="outline" className="mt-3">
            <Plus className="h-4 w-4 mr-2" />
            Create your first QR code
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {qrCodes.map((qr) => (
            <div
              key={qr.id}
              onClick={() => openEdit(qr)}
              className="group relative p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-4">
                {/* QR Preview */}
                <div className="w-16 h-16 rounded-md bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden">
                  {qr.image_url ? (
                    <img src={qr.image_url} alt={qr.label} className="w-full h-full object-contain" />
                  ) : (
                    <QrCode className="h-8 w-8 text-muted-foreground/30" />
                  )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-foreground truncate">{qr.label}</h3>
                    {!qr.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{qr.destination_url}</p>
                  {qr.unit_code && (
                    <p className="text-xs text-[#8FB560] mt-0.5">Unit: {qr.unit_code}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground font-mono">{qr.short_code}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <BarChart3 className="h-3 w-3" />
                      {qr.scan_count} scan{qr.scan_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions overlay — always visible on touch (no hover); hover-reveal from sm up */}
              <div className="absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={(e) => { e.stopPropagation(); copyUrl(qr.short_code); }}
                  title="Copy redirect URL"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                {isSuperadmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDelete(qr); }}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Per-Unit QR Codes (auto-generated check-in QRs across the fleet). */}
      <UnitQrCodeList />

      {/* Create/Edit Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingQR ? `Edit: ${editingQR.label}` : 'Create QR Code'}
        description={editingQR ? `Short code: ${editingQR.short_code}` : 'Design a styled QR code'}
      >
        <div className="space-y-6 p-6">
          {/* Label */}
          <div className="space-y-2">
            <Label>Label</Label>
            <Input
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              placeholder="e.g., Unit 1RE001 Check-In"
              className="bg-background border-border"
            />
          </div>

          {/* Destination URL */}
          <div className="space-y-2">
            <Label>Destination URL</Label>
            <Input
              value={formDestination}
              onChange={(e) => setFormDestination(e.target.value)}
              placeholder="https://app.hollywoodtrucks.com/check-in/..."
              className="bg-background border-border"
            />
            {editingQR && (
              <p className="text-xs text-muted-foreground">
                QR encodes: <span className="font-mono">{getRedirectUrl(editingQR.short_code)}</span>
              </p>
            )}
          </div>

          {/* Scan stats */}
          {editingQR && (
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{editingQR.scan_count} scan{editingQR.scan_count !== 1 ? 's' : ''}</span>
              </div>
              {isSuperadmin && editingQR.scan_count > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResetStats(editingQR)}
                  className="h-7 text-xs text-muted-foreground"
                >
                  Reset
                </Button>
              )}
            </div>
          )}

          {/* QR Designer */}
          <div className="border-t border-border pt-4 space-y-2">
            {!editingQR && formDestination.trim() && (
              <p className="text-xs text-muted-foreground">
                Preview encodes your destination URL. After you create this QR code, the preview will use the tracked redirect URL for scan counting.
              </p>
            )}
            <QRCodeDesigner
              ref={designerRef}
              value={qrValue}
              config={formConfig}
              onConfigChange={setFormConfig}
              size={240}
              showDesignControls
              showExport
              showLogoControls
              onSaveImage={editingQR ? handleSaveImage : undefined}
              isSaving={isSubmitting}
              logos={logos}
              onUploadLogo={uploadLogo}
              isUploadingLogo={isUploading}
              onDeleteLogo={deleteLogo}
            />
          </div>

          {/* Save button */}
          <div className="flex gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={() => setDrawerOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting || !formLabel.trim() || !formDestination.trim()}
              className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingQR ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </div>
      </AdminFormDrawer>
    </div>
  );
}
